import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Stethoscope,
  ArrowLeft,
  FileText,
  Download,
  Video,
  CheckCircle,
  Clock,
  Copy,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ContentLibraryPage() {
  const { doctorId } = useParams();
  const navigate = useNavigate();
  const [scripts, setScripts] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [templateDialog, setTemplateDialog] = useState(false);
  const [selectedScript, setSelectedScript] = useState(null);
  const [templateName, setTemplateName] = useState("");
  const [performanceDialog, setPerformanceDialog] = useState(false);
  const [performanceRating, setPerformanceRating] = useState("");
  const [performanceNotes, setPerformanceNotes] = useState("");

  useEffect(() => {
    fetchScripts();
  }, [doctorId]);

  const fetchScripts = async () => {
    try {
      const response = await axios.get(`${API}/scripts/${doctorId}`);
      setScripts(response.data.scripts || []);
    } catch (error) {
      console.error("Error fetching scripts:", error);
      toast.error("Failed to load content library");
    } finally {
      setLoading(false);
    }
  };

  const filteredScripts = scripts.filter((script) =>
    script.topic.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const getRiskColor = (status) => {
    if (!status) return "bg-gray-100 text-gray-600";
    switch (status.toLowerCase()) {
      case "low":
        return "bg-green-100 text-green-700";
      case "medium":
        return "bg-yellow-100 text-yellow-700";
      case "high":
        return "bg-red-100 text-red-700";
      default:
        return "bg-gray-100 text-gray-600";
    }
  };

  const handleExport = (script) => {
    navigate(`/export/${doctorId}`, { state: { script } });
  };

  const handleSaveAsTemplate = (script) => {
    setSelectedScript(script);
    setTemplateName(script.topic);
    setTemplateDialog(true);
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) {
      toast.error("Please enter a template name");
      return;
    }

    try {
      await axios.post(
        `${API}/scripts/${selectedScript.id}/save-as-template?template_name=${encodeURIComponent(templateName)}`,
      );
      toast.success("Template saved successfully!");
      setTemplateDialog(false);
      setTemplateName("");
      setSelectedScript(null);
    } catch (error) {
      console.error("Error saving template:", error);
      toast.error("Failed to save template");
    }
  };

  const handleMarkPerformance = (script) => {
    setSelectedScript(script);
    setPerformanceRating(script.performance_rating || "");
    setPerformanceNotes(script.performance_notes || "");
    setPerformanceDialog(true);
  };

  const handleSavePerformance = async () => {
    if (!performanceRating) {
      toast.error("Please select a performance rating");
      return;
    }

    try {
      await axios.put(`${API}/scripts/${selectedScript.id}/performance`, {
        performance_rating: performanceRating,
        performance_notes: performanceNotes,
      });
      toast.success("Performance updated!");
      setPerformanceDialog(false);
      setPerformanceRating("");
      setPerformanceNotes("");
      setSelectedScript(null);
      fetchScripts();
    } catch (error) {
      console.error("Error updating performance:", error);
      toast.error("Failed to update performance");
    }
  };

  const getPerformanceColor = (rating) => {
    const colors = {
      high: "bg-green-100 text-green-700",
      medium: "bg-yellow-100 text-yellow-700",
      low: "bg-red-100 text-red-700",
    };
    return colors[rating] || "";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#2C5F44] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[#5C7063]">Loading content library...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7] grain-texture">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate(`/dashboard/${doctorId}`)}
              variant="ghost"
              size="sm"
              className="rounded-full"
              data-testid="library-back-btn"
            >
              <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
            </Button>
            <Stethoscope className="w-8 h-8 text-[#2C5F44]" strokeWidth={1.5} />
            <span
              className="text-2xl font-bold text-[#2C5F44]"
              style={{ fontFamily: "Nunito, sans-serif" }}
            >
              Content Library
            </span>
          </div>
          <Button
            onClick={() => navigate(`/script/${doctorId}`)}
            className="button-pill bg-[#2C5F44] hover:bg-[#244b36] text-white rounded-full"
            data-testid="library-create-new-btn"
          >
            Create New Script
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <h1
            className="text-4xl font-bold text-[#1C2A21] mb-2"
            style={{ fontFamily: "Nunito, sans-serif" }}
          >
            Your Content Library
          </h1>
          <p className="text-lg text-[#5C7063] mb-6">
            {scripts.length} script{scripts.length !== 1 ? "s" : ""} created
          </p>

          {/* Search */}
          <Input
            placeholder="Search by topic..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="max-w-md rounded-xl border-[#2C5F44]/20"
            data-testid="library-search-input"
          />
        </motion.div>

        {filteredScripts.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center py-20"
          >
            <div className="w-24 h-24 bg-[#82A08B]/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <FileText
                className="w-12 h-12 text-[#2C5F44]"
                strokeWidth={1.5}
              />
            </div>
            <h2
              className="text-3xl font-bold text-[#1C2A21] mb-4"
              style={{ fontFamily: "Nunito, sans-serif" }}
            >
              {searchQuery ? "No scripts found" : "No Scripts Yet"}
            </h2>
            <p className="text-lg text-[#5C7063] mb-8 max-w-2xl mx-auto">
              {searchQuery
                ? "Try a different search term"
                : "Create your first script to get started"}
            </p>
            {!searchQuery && (
              <Button
                onClick={() => navigate(`/script/${doctorId}`)}
                className="button-pill bg-[#2C5F44] hover:bg-[#244b36] text-white rounded-full px-8 py-6 text-lg"
                data-testid="library-empty-create-btn"
              >
                Create Your First Script
              </Button>
            )}
          </motion.div>
        ) : (
          <div className="space-y-4">
            {filteredScripts.map((script, index) => (
              <motion.div
                key={script.id || index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                className="bg-white rounded-3xl p-6 card-hover border border-[#2C5F44]/10"
                data-testid={`library-script-${index}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <h3
                        className="text-xl font-bold text-[#1C2A21]"
                        style={{ fontFamily: "Nunito, sans-serif" }}
                      >
                        {script.topic}
                      </h3>
                      {script.compliance_status && (
                        <Badge
                          className={`${getRiskColor(script.compliance_status)} rounded-full`}
                        >
                          {script.compliance_status} Risk
                        </Badge>
                      )}
                    </div>
                    <p className="text-[#5C7063] mb-4 line-clamp-2">
                      {script.hook}
                    </p>
                    <div className="flex items-center gap-4 text-sm text-[#5C7063]">
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" strokeWidth={1.5} />
                        {new Date(script.created_at).toLocaleDateString()}
                      </span>
                      <span className="flex items-center gap-1">
                        <FileText className="w-4 h-4" strokeWidth={1.5} />
                        {script.language}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleExport(script)}
                      className="button-pill bg-[#2C5F44] hover:bg-[#244b36] text-white rounded-full"
                      data-testid={`library-export-btn-${index}`}
                    >
                      <Download className="w-4 h-4 mr-2" strokeWidth={1.5} />
                      Export
                    </Button>
                    <Button
                      onClick={() => handleSaveAsTemplate(script)}
                      variant="outline"
                      className="rounded-full border-[#2C5F44]/20"
                      data-testid={`library-template-btn-${index}`}
                    >
                      <Copy className="w-4 h-4 mr-2" strokeWidth={1.5} />
                      Save as Template
                    </Button>
                    <Button
                      onClick={() => {
                        localStorage.setItem(
                          `selectedVideoScript:${doctorId}`,
                          JSON.stringify(script),
                        );
                        navigate(`/video/${doctorId}`, { state: { script } });
                      }}
                      variant="outline"
                      className="rounded-full border-[#2C5F44]/20"
                      data-testid={`library-video-btn-${index}`}
                    >
                      <Video className="w-4 h-4 mr-2" strokeWidth={1.5} />
                      Video
                    </Button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Save as Template Dialog */}
      <Dialog open={templateDialog} onOpenChange={setTemplateDialog}>
        <DialogContent className="bg-white rounded-3xl">
          <DialogHeader>
            <DialogTitle
              className="text-2xl font-bold text-[#1C2A21]"
              style={{ fontFamily: "Nunito, sans-serif" }}
            >
              Save as Template
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-[#5C7063]">
              Save this script structure as a reusable template for faster
              content creation.
            </p>
            <div>
              <label className="text-sm font-medium text-[#1C2A21] mb-2 block">
                Template Name
              </label>
              <Input
                placeholder="e.g., Heart Health FAQ Template"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                className="rounded-xl border-[#2C5F44]/20"
                data-testid="library-template-name-input"
              />
            </div>
            <Button
              onClick={handleSaveTemplate}
              className="button-pill bg-[#2C5F44] hover:bg-[#244b36] text-white rounded-full w-full"
              data-testid="library-save-template-btn"
            >
              <Copy className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Save Template
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
